import { slugify, toText } from "./text";
import { OBJECTIVE_NONE_CODE, type ObjectiveValue, type SourceRow } from "./types";

/** Source columns that may hold an intervention's objectives. */
const OBJECTIVE_COLUMNS = ["Objectives", "Objective"] as const;

const OBJECTIVE_PALETTE = [
  "#d1495b",
  "#2e86ab",
  "#3fb68b",
  "#f4a259",
  "#7c6fcf",
  "#5a8f29",
  "#c86b98",
  "#6b7280",
];

export function getObjectiveRawValue(row: SourceRow): string {
  for (const column of OBJECTIVE_COLUMNS) {
    const value = toText(row[column]);
    if (value) return value;
  }
  return "";
}

/**
 * Parses an objectives cell such as "1 Rusforebygging; 2: Vold" into codes
 * with optional labels. Non-numeric entries get a `txt_<slug>` code.
 */
function parseObjectiveEntries(raw: unknown): { code: string; label: string }[] {
  const entries: { code: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const part of toText(raw)
    .split(/[\n;]+/)
    .map((s) => s.trim())
    .filter(Boolean)) {
    const match = part.match(/^(\d+)\s*(?:[-.):]\s*)?(.*)$/);
    const code = match ? match[1]! : `txt_${slugify(part)}`;
    if (code === "txt_" || seen.has(code)) continue;
    seen.add(code);
    entries.push({ code, label: match ? (match[2] ?? "").trim() : part });
  }
  return entries;
}

/** Removes parenthesised groups (including nested ones) from a label. */
function cleanObjectiveLabel(label: string): string {
  let depth = 0;
  let out = "";
  for (const ch of label) {
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if (depth === 0) out += ch;
  }
  return out.replace(/\s{2,}/g, " ").trim();
}

function normalizeObjectiveCode(code: string): string {
  const value = code.trim();
  return !value || value === "0" ? OBJECTIVE_NONE_CODE : value;
}

function getObjectiveColor(code: string): string {
  if (code === OBJECTIVE_NONE_CODE) return "#cccccc";
  const numeric = Number(code);
  const index =
    Number.isFinite(numeric) && numeric > 0 ? (numeric - 1) % OBJECTIVE_PALETTE.length : 0;
  return OBJECTIVE_PALETTE[index]!;
}

/**
 * Registers (or improves the label of) an objective in `registry` and returns
 * its normalized code.
 */
function registerObjective(
  registry: Record<string, ObjectiveValue>,
  rawCode: string,
  rawLabel: string,
): string {
  const code = normalizeObjectiveCode(rawCode);
  if (code === OBJECTIVE_NONE_CODE) {
    registry[code] = { label: { no: "Ingen", en: "None" }, color: "#cccccc" };
    return code;
  }
  const label = cleanObjectiveLabel(rawLabel);
  const existing = registry[code];
  if (existing) {
    if (label && (!existing.label.no || /^\.{3}$/.test(existing.label.no))) {
      existing.label = { no: label, en: label };
    }
    return code;
  }
  const resolved = label || `Maalsetting ${code}`;
  registry[code] = { label: { no: resolved, en: resolved }, color: getObjectiveColor(code) };
  return code;
}

/** All objective codes for a row; blank cells map to the "none" objective. */
export function parseRowObjectives(
  row: SourceRow,
  registry: Record<string, ObjectiveValue>,
): string[] {
  const raw = getObjectiveRawValue(row);
  const codes = new Set<string>();
  for (const entry of parseObjectiveEntries(raw))
    codes.add(registerObjective(registry, entry.code, entry.label));
  // Every leading number in the cell counts, whatever delimiter was used.
  for (const part of raw.split(/[\n;]+/)) {
    const number = part.trim().match(/^(\d+)/)?.[1];
    if (number) codes.add(registerObjective(registry, number, ""));
  }
  if (!codes.size) codes.add(registerObjective(registry, OBJECTIVE_NONE_CODE, ""));
  return [...codes];
}
